import { KikCommonUtils, KikHttpStatusCodes } from "@kikocosmeticsorg/uc-api-nest-common-fe";
import { getServiceSettings, setServiceSetting } from "@springtree/eva-sdk-core-service";
import {
  OrdersAdditionalOrderDataOptions,
  SvcGetActiveShoppingCart,
  SvcGetShoppingCart,
} from "@springtree/eva-services-core";
import { useQuery } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { isLoadingCartAtom, isPendingCartAtom, shoppingCartAtom, shoppingCartRequestAtom } from "~/atoms/cart";
import { useAuthContext } from "~/contexts/auth";
import { useConfiguration } from "~/contexts/configuration";
import { useEvaService } from "~/hooks/use-eva-service";
import { useGuestCartStorage } from "~/hooks/use-guest-cart-storage";
import Logger from "~/services/logger/logger";
import { StorageFactory } from "~/services/storage.factory";
import { CacheKeys } from "~/shared/cache-keys";
import Constants from "~/utils/constants";

export default function CartSync() {
  const [, setCart] = useAtom(shoppingCartAtom);
  const [, setIsLoadingCart] = useAtom(isLoadingCartAtom);
  const [, setIsPendingCart] = useAtom(isPendingCartAtom);
  const [cartRequest, setCartRequest] = useAtom(shoppingCartRequestAtom);
  const { user, isLoadingUser, currentUserToken } = useAuthContext();
  const { organizationUnitId } = useConfiguration();
  const getShoppingCart = useEvaService(SvcGetShoppingCart);
  const getActiveShoppingCart = useEvaService(SvcGetActiveShoppingCart);
  const cache = useGuestCartStorage();
  const session = useMemo(() => new StorageFactory<CacheKeys>("sessionStorage"), []);

  const clearAnonymousStuff = useCallback(() => {
    Logger.instance.debug("CartSync: clearing guest cart keys from localStorage");
    cache.remove(CacheKeys.ANONYMOUS_ORDER_ID);
    cache.remove(CacheKeys.ANONYMOUS_APP_TOKEN);
  }, [cache]);

  // Track whether the user was previously logged in, to detect cross-tab logout
  const wasLoggedInRef = useRef(false);
  // Ref to read cartRequest without triggering re-runs of the restore/clear effect
  const cartRequestRef = useRef(cartRequest?.OrderID);
  useEffect(() => {
    cartRequestRef.current = cartRequest?.OrderID;
  }, [cartRequest?.OrderID]);

  // Restore cart from localStorage on mount / clear cart on logout
  // Deps: only `user` (login/logout transitions) and `isLoadingUser` (initial load gate)
  useEffect(() => {
    // Set by cleanup when this effect re-runs (e.g. on logout).
    // Async ops (attachAndLoad / loadActiveCart) check it before writing cartRequest
    // so they don't race past a subsequent logout cleanup.
    let cancelled = false;
    if (currentUserToken) {
      wasLoggedInRef.current = true;

      const storedOrderId = cache.get(CacheKeys.ANONYMOUS_ORDER_ID);
      if (storedOrderId) {
        // Case 1: User had a guest cart - attach it to the logged user
        const attachAndLoad = async () => {
          try {
            // A GET SHOPPING CART W/ TOKEN ON AN ANONYMOUS CART WILL AUTOMAGICALLY LINK THE CART
            await getShoppingCart({ OrderID: storedOrderId });
            if (!cancelled) {
              setCartRequest({ OrderID: storedOrderId });
            }
          } catch (error) {
            Logger.instance.error("CartSync: Guest cart attach failed, falling back to active cart", {
              orderId: storedOrderId,
              error,
            });
            // Fallback: fetch active cart from EVA
            try {
              const activeCart = await getActiveShoppingCart();
              if (activeCart?.OrderID && !cancelled) {
                setCartRequest({ OrderID: activeCart.OrderID });
              }
            } catch (fallbackError) {
              Logger.instance.error("CartSync: Failed to fetch active cart as fallback", fallbackError);
            }
          }
        };
        attachAndLoad();
      } else if (!cartRequestRef.current) {
        // Case 2: No guest cart - fetch active cart from EVA for logged user

        const loadActiveCart = async () => {
          try {
            Logger.instance.debug("CartSync: Fetching active cart for logged user");
            const activeCart = await getActiveShoppingCart();
            if (activeCart?.OrderID) {
              const cartData = await getShoppingCart({ OrderID: activeCart.OrderID });
              // Only load cart if it's not placed/paid
              if (!cartData?.ShoppingCart?.IsPlaced) {
                if (!cancelled) {
                  setCartRequest({ OrderID: activeCart.OrderID });
                }
              }
            }
          } catch (error) {
            Logger.instance.warn("CartSync: Failed to fetch active cart for logged user", error);
          }
        };
        loadActiveCart();
      }
      // Always clean up guest cart keys once user is logged in
      clearAnonymousStuff();
    } else if (wasLoggedInRef.current && !user) {
      // User just logged out (was logged in before, now null) — clear the cart
      // This covers both same-tab logout and cross-tab logout via storage event
      wasLoggedInRef.current = false;
      // Clear cart state
      if (cartRequestRef.current) {
        setCartRequest(undefined);
      }
      setCart(undefined);

      // Clean up all guest cart references from localStorage
      clearAnonymousStuff();
      cache.remove(CacheKeys.ANONYMOUS_ORDER_COMPLETED);
    } else if (!cartRequestRef.current) {
      // Guest restore: only on mount (cartRequestRef is still undefined)
      const storedOrderId = cache.get(CacheKeys.ANONYMOUS_ORDER_ID);
      const storedAppToken = cache.get(CacheKeys.ANONYMOUS_APP_TOKEN);
      const hasCompletedOrder = cache.get(CacheKeys.ANONYMOUS_ORDER_COMPLETED);

      if (storedOrderId && !hasCompletedOrder) {
        if (storedAppToken) {
          try {
            const tokenData = KikCommonUtils.parseJsonSafely<{ token: string; origin: string }>(storedAppToken)!;
            const { token, origin } = tokenData;

            if (token && origin) {
              const settings = getServiceSettings();
              setServiceSetting("applicationTokens", {
                ...(settings.applicationTokens || {}),
                [origin]: token,
              });
            }
          } catch (tokenError) {
            Logger.instance.error("CartSync: Failed to restore application token", {
              error: tokenError,
            });
          }

          setCartRequest({ OrderID: storedOrderId });
        } else if (storedOrderId && hasCompletedOrder) {
          clearAnonymousStuff();
        }
      }
    }

    return () => {
      cancelled = true;
    };
  }, [
    cache,
    clearAnonymousStuff,
    currentUserToken,
    getActiveShoppingCart,
    getShoppingCart,
    isLoadingUser,
    setCart,
    setCartRequest,
    user,
  ]);

  // Persist cart OrderID to localStorage when it changes (for guest users only)
  // Note: Application token is persisted in use-eva-cart.ts when the order is created
  const hasRestoredRef = useRef(false);

  useEffect(() => {
    // Only persist to localStorage for guest users (Incognito)
    if (currentUserToken) {
      hasRestoredRef.current = false;
      return;
    }

    if (cartRequest?.OrderID) {
      cache.set(CacheKeys.ANONYMOUS_ORDER_ID, cartRequest.OrderID);
      hasRestoredRef.current = true;
    } else if (hasRestoredRef.current) {
      // Cart cleared - remove both OrderID and application token
      clearAnonymousStuff();
    }
  }, [currentUserToken, cartRequest?.OrderID, cache, clearAnonymousStuff]);

  const { data, isInitialLoading, error } = useQuery({
    queryKey: ["shoppingCart", cartRequest?.OrderID, currentUserToken || "guest"],
    // Cart data should stay fresh for 10 seconds
    // This reduces redundant fetches while keeping data reasonably current
    staleTime: 10 * 1000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      if (!cartRequest?.OrderID) {
        return undefined;
      }
      try {
        Logger.instance.debug(`CartSync: Fetching shopping cart ${cartRequest.OrderID}`);

        const shoppingCart = await getShoppingCart({
          OrderID: cartRequest.OrderID,
          AdditionalOrderDataOptions: {
            ProductProperties: Constants.CART_INCLUDED_FIELDS,
            IncludeProductRequirements: true,
            IncludeAvailablePaymentMethods: true,
            IncludeAvailableShippingMethods: true,
            IncludePickProductOptions: true,
            IncludePrefigureDiscounts: true,
          } as OrdersAdditionalOrderDataOptions,
        });
        if (shoppingCart?.ShoppingCart) {
          session.set(CacheKeys.CART_PRODUCTS_NUMBER, shoppingCart?.ShoppingCart?.TotalItems);
        }
        return shoppingCart;
      } catch (err: any) {
        const status = err?.response?.status;
        // If auth error, clear the cart to stop retry loop
        if (status === KikHttpStatusCodes.Unauthorized || status === KikHttpStatusCodes.Forbidden) {
          Logger.instance.warn("CartSync: Auth error, clearing cart to prevent retry loop", {
            status,
          });
          setCartRequest(undefined);
          clearAnonymousStuff();
          return undefined; // Return undefined instead of throwing to prevent error state
        }

        throw err;
      }
    },
    enabled: !!cartRequest?.OrderID && !isLoadingUser,
    retry: (failureCount, error: any) => {
      // Don't retry on auth errors
      const status = error?.response?.status;
      if (status === KikHttpStatusCodes.Unauthorized || status === KikHttpStatusCodes.Forbidden) {
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
  });

  // Clear guest cart when country changes (based on EVA organizationUnitId)
  useEffect(() => {
    if (
      data?.ShoppingCart?.OriginatingOrganizationUnitID &&
      data?.ShoppingCart?.OriginatingOrganizationUnitID !== organizationUnitId
    ) {
      Logger.instance.info("CartSync: country changed, clearing cart");
      setCartRequest(void 0);
      clearAnonymousStuff();
    }
  }, [
    organizationUnitId,
    cache,
    setCartRequest,
    data?.ShoppingCart?.OriginatingOrganizationUnitID,
    clearAnonymousStuff,
  ]);

  useEffect(() => {
    // cartRequest cleared (e.g. logout) — clear cart regardless of what React Query holds.
    // React Query keeps previous `data` alive when the new key's query is disabled,
    // so we must check cartRequest first to avoid re-setting cart from stale data.
    if (!cartRequest?.OrderID) {
      setCart(undefined);
      return;
    }

    if (data) {
      setCart(data);
    }
  }, [data, cartRequest?.OrderID, setCart]);

  useEffect(() => {
    // If there is no cart to load (!cartRequest?.OrderID), isLoadingCart must be false
    setIsLoadingCart(!!cartRequest?.OrderID && isInitialLoading);
  }, [isInitialLoading, setIsLoadingCart, cartRequest?.OrderID]);

  useEffect(() => {
    setIsPendingCart((!!cartRequest?.OrderID && !data) || isInitialLoading);
  }, [isInitialLoading, data, setIsPendingCart, cartRequest?.OrderID]);

  useEffect(() => {
    if (error) {
      Logger.instance.error("CartSync: Error loading cart", error);
    }
  }, [error]);

  // Listen for localStorage changes from other tabs to sync OrderID and application token
  useEffect(() => {
    const handleStorageChange = (event: { newValue: string | null }) => {
      // Once logged in, guest cart keys are irrelevant — the cart has been attached
      // server-side. Ignore cross-tab cleanup events that would otherwise clear it.
      if (currentUserToken) {
        return;
      }

      const newOrderId = event.newValue;

      if (newOrderId && newOrderId !== cartRequest?.OrderID) {
        const storedAppToken = cache.get(CacheKeys.ANONYMOUS_APP_TOKEN);
        if (storedAppToken) {
          try {
            const tokenData = KikCommonUtils.parseJsonSafely<{ token: string; origin: string }>(storedAppToken)!;
            const { token, origin } = tokenData;

            if (token && origin) {
              const settings = getServiceSettings();
              const currentTokens = settings.applicationTokens || {};

              setServiceSetting("applicationTokens", {
                ...currentTokens,
                [origin]: token,
              });
            }
          } catch (tokenError) {
            Logger.instance.error("CartSync: Failed to sync application token", tokenError);
          }
        }
        setCartRequest({ OrderID: newOrderId });
      } else if (!newOrderId && cartRequest?.OrderID) {
        setCartRequest(undefined);
      }
    };
    return cache.subscribe(CacheKeys.ANONYMOUS_ORDER_ID, handleStorageChange);
  }, [currentUserToken, cartRequest?.OrderID, setCartRequest, cache, user?.AccountType]);

  return null;
}
